import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import PlaceIcon from '@mui/icons-material/Place';
import BoltIcon from '@mui/icons-material/Bolt';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import IconButton from '@mui/material/IconButton';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import InputAdornment from '@mui/material/InputAdornment';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import QuizIcon from '@mui/icons-material/Quiz';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Badge, Issuer } from '@tinyrewards/shared';
import { getIssuers } from '../api/issuers';
import { createBadge, getBadges, updateBadge, uploadBadgeImage, saveTimedQuestions } from '../api/badges';
import { fetchUrlInfo, generateQuestions, generateCriteria, generateBadgeDetails, QuizQuestion } from '../api/ai';
import { extractYouTubeId, getYoutubeDetails, loadYoutubeTranscript, type TimedQuestion, type TranscriptSegment } from '../api/youtube';
import SimpleHtmlEditor from '../components/SimpleHtmlEditor';
import BadgeEditor from '../components/BadgeEditor';
import LocationPicker from '../components/LocationPicker';
import BadgeDesignAssistant from '../components/BadgeDesignAssistant';
import TimedQuestionsStep from '../components/TimedQuestionsStep';
import { BADGE_TYPES, dbBadgeTypeToWizard, type BadgeType } from '../constants/badgeTypes';
import { PURPLE_AVATAR } from '../theme/tokens';

// ---------------------------------------------------------------------------
// Badge types — canonical list lives in constants/badgeTypes. Local map adds
// the wizard's per-type icon since the shared module is icon-free.
// ---------------------------------------------------------------------------

const ICON_BY_TYPE: Record<BadgeType, React.ReactNode> = {
    read:    <MenuBookIcon sx={{ fontSize: 36 }} />,
    watch:   <OndemandVideoIcon sx={{ fontSize: 36 }} />,
    geo:     <PlaceIcon sx={{ fontSize: 36 }} />,
    instant: <BoltIcon sx={{ fontSize: 36 }} />,
    manual:  <EmojiEventsIcon sx={{ fontSize: 36 }} />,
    multi:   <AccountTreeIcon sx={{ fontSize: 36 }} />,
};

// ---------------------------------------------------------------------------
// Step IDs — each badge type uses a subset
// ---------------------------------------------------------------------------

type StepId = 'type' | 'url' | 'details' | 'tags' | 'image' | 'criteria' | 'presentation' | 'questions' | 'badges' | 'preview';

const STEP_LABELS: Record<StepId, string> = {
    type:         'Badge Type',
    url:          'Source URL',
    details:      'Details',
    tags:         'Tags',
    image:        'Image',
    criteria:     'Criteria & Settings',
    presentation: 'Presentation',
    questions:    'Questions',
    badges:       'Select Badges',
    preview:      'Preview',
};

// Badge types that support optional quiz + presentation add-ons
const SUPPORTS_ADDONS: BadgeType[] = ['read', 'watch', 'geo', 'instant'];

function getStepsForType(
    badgeType: BadgeType | null,
    includePresentation: boolean,
    includeQuiz: boolean,
    editMode = false,
): StepId[] {
    if (!badgeType) return ['type'];

    // Edit mode: only legacy-parity steps (no type re-pick, no presentation/
    // questions/multi-badges add-ons). Source URL is locked once a badge exists.
    if (editMode) {
        return ['details', 'tags', 'image', 'criteria', 'preview'];
    }

    const base: StepId[] = (() => {
        switch (badgeType) {
            case 'read':
            case 'watch':
                return ['type', 'url', 'details', 'tags', 'image', 'criteria'];
            case 'geo':
            case 'instant':
                return ['type', 'details', 'tags', 'image', 'criteria'];
            case 'multi':
                return ['type', 'details', 'tags', 'image', 'criteria', 'badges', 'preview'];
            case 'manual':
                return ['type', 'details', 'tags', 'image', 'criteria', 'preview'];
        }
    })();

    if (!SUPPORTS_ADDONS.includes(badgeType)) return base;

    const optional: StepId[] = [];
    if (includePresentation) optional.push('presentation');
    if (includeQuiz)         optional.push('questions');

    return [...base, ...optional, 'preview'];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function stripHtml(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent ?? '';
}

interface BadgeFormPageProps {
    initialBadge?: Badge;
}

export default function CreateBadgePage({ initialBadge }: BadgeFormPageProps = {}) {
    const editMode = !!initialBadge;
    const navigate = useNavigate();
    const [stepIndex, setStepIndex] = useState(0);

    // Badge type — locked in edit mode (badge type is immutable post-creation)
    const [badgeType, setBadgeType] = useState<BadgeType | null>(
        initialBadge ? dbBadgeTypeToWizard(initialBadge.badgeType) : null,
    );

    // URL (read / watch). Prefilled from Pages.Url (Badge.targetUrl) in edit mode.
    const [sourceUrl, setSourceUrl] = useState(initialBadge?.targetUrl ?? '');

    // Details
    const [badgeName, setBadgeName] = useState(initialBadge?.badgeName ?? '');
    const [badgeDescription, setBadgeDescription] = useState(initialBadge?.badgeDescription ?? '');
    const [issuerID, setIssuerID] = useState(initialBadge?.userID ?? '');
    const [issuers, setIssuers] = useState<Issuer[]>([]);

    // Tags — joined from string[] back to comma-separated for the input
    const [tags, setTags] = useState((initialBadge?.tags ?? []).join(', '));

    // Image
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(initialBadge?.badgeImage ?? null);
    const [editorOpen, setEditorOpen] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Criteria & Settings
    const [badgeCriteria, setBadgeCriteria] = useState(initialBadge?.badgeCriteria ?? '');
    const [isPublic, setIsPublic] = useState(initialBadge?.isPublic ?? false);
    const [isActive, setIsActive] = useState(initialBadge?.isActive ?? true);

    // Geo location (Find-to-Earn)
    const [geoLocation, setGeoLocation] = useState<{ lat: number; lng: number } | null>(
        initialBadge?.geoLocation
            ? { lat: initialBadge.geoLocation.latitude, lng: initialBadge.geoLocation.longitude }
            : null,
    );
    const [geoRadius, setGeoRadius] = useState(initialBadge?.geoLocation?.radius ?? 250);

    // Optional add-ons (all types except manual / multi)
    const [includeQuiz, setIncludeQuiz] = useState(false);
    const [includePresentation, setIncludePresentation] = useState(false);

    // Presentation step
    const [presentationType, setPresentationType] = useState<'youtube' | 'powerpoint' | 'scorm' | null>(null);
    const [presentationUrl, setPresentationUrl] = useState('');
    const [presentationFile, setPresentationFile] = useState<File | null>(null);
    const presentationFileRef = useRef<HTMLInputElement>(null);

    // AI-generated questions (read-to-earn)
    const [questions, setQuestions] = useState<QuizQuestion[]>([]);

    // Quiz / objective settings (read-to-earn schema: VisitScroll)
    const [quizScore, setQuizScore] = useState('80');
    const [randomizeQuestions, setRandomizeQuestions] = useState(true);
    const [timedQuestions, setTimedQuestions] = useState(false);
    // Watch-with-timed-questions: the editor's working set, in the
    // extension's Quiz-modal shape (timeStamp HH:MM:SS, options w/ isCorrect).
    const [timedQuestionsList, setTimedQuestionsList] = useState<TimedQuestion[]>([]);

    // Read tracking settings
    const [trackingMode, setTrackingMode] = useState<'auto' | 'signoff'>('auto');
    const [scrollPercentage, setScrollPercentage] = useState('100');
    const [signoffText, setSignoffText] = useState('I Have Read and Understand');
    const [signoffTheme, setSignoffTheme] = useState<'dark' | 'light'>('light');
    const [signoffPos, setSignoffPos] = useState<'left' | 'middle' | 'right'>('right');
    const [signoffPosV, setSignoffPosV] = useState<'fixed' | 'sticky'>('sticky');
    const [minReadTimeEnabled, setMinReadTimeEnabled] = useState(false);
    const [minReadTimeHours, setMinReadTimeHours] = useState('0');
    const [minReadTimeMinutes, setMinReadTimeMinutes] = useState('0');
    const [minReadTimeSeconds, setMinReadTimeSeconds] = useState('0');

    // Learning path / multi badge
    interface LearningPathBadge { id: string; name: string; isRequired: boolean; image?: string }
    const [selectedBadges, setSelectedBadges] = useState<LearningPathBadge[]>([]);
    const [availableBadges, setAvailableBadges] = useState<Badge[]>([]);
    const [badgeSearchQuery, setBadgeSearchQuery] = useState('');
    const [loadingBadges, setLoadingBadges] = useState(false);
    const [optionalRequiredCount, setOptionalRequiredCount] = useState(0);

    // URL lookup tracking — avoid re-fetching the same URL
    const [lastFetchedUrl, setLastFetchedUrl] = useState('');
    // For Watch badges: cache the transcript loaded at URL-check time so the
    // Questions step doesn't refetch. Keyed by videoId so a URL change
    // replaces (not appends to) the cache.
    const transcriptCacheRef = useRef<{ videoId: string; segments: TranscriptSegment[] } | null>(null);
    const [cachedTranscriptVideoId, setCachedTranscriptVideoId] = useState<string | null>(null);
    // Question generation tracking — avoid re-generating for the same URL
    const [lastGeneratedQuestionsUrl, setLastGeneratedQuestionsUrl] = useState('');

    // AI loading states
    const [fetchingUrl, setFetchingUrl] = useState(false);
    const [generatingDetails, setGeneratingDetails] = useState(false);
    const [generatingQuestions, setGeneratingQuestions] = useState(false);
    const [generatingCriteria, setGeneratingCriteria] = useState(false);
    const [designAssistantOpen, setDesignAssistantOpen] = useState(false);

    // Track whether we've already auto-generated details for this wizard session
    const detailsGeneratedRef = useRef(false);

    // State
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const steps = useMemo(
        () => getStepsForType(badgeType, includePresentation, includeQuiz, editMode),
        [badgeType, includePresentation, includeQuiz, editMode],
    );
    const currentStepId = steps[stepIndex] ?? 'type';
    const stepLabels = steps.map(s => STEP_LABELS[s]);
    const isLastStep = stepIndex === steps.length - 1;

    useEffect(() => {
        getIssuers().then(res => {
            if (res.success && res.data) {
                setIssuers(res.data);
                // In edit mode the issuer is already pinned from initialBadge — don't override.
                if (!editMode && res.data.length === 1) {
                    setIssuerID(res.data[0].issuerID);
                }
            }
        });
    }, [editMode]);

    const reset = () => {
        setStepIndex(0);
        setBadgeType(null);
        setSourceUrl('');
        setLastFetchedUrl('');
        setLastGeneratedQuestionsUrl('');
        setBadgeName('');
        setBadgeDescription('');
        setIssuerID(issuers.length === 1 ? issuers[0].issuerID : '');
        setTags('');
        setImageFile(null);
        setImagePreview(null);
        setBadgeCriteria('');
        setIsPublic(false);
        setQuestions([]);
        setQuizScore('80');
        setRandomizeQuestions(true);
        setTimedQuestions(false);
        setTrackingMode('auto');
        setScrollPercentage('100');
        setSignoffText('I Have Read and Understand');
        setSignoffTheme('light');
        setSignoffPos('right');
        setSignoffPosV('sticky');
        setMinReadTimeEnabled(false);
        setMinReadTimeHours('0');
        setMinReadTimeMinutes('0');
        setMinReadTimeSeconds('0');
        setSelectedBadges([]);
        setAvailableBadges([]);
        setBadgeSearchQuery('');
        setOptionalRequiredCount(0);
        setGeoLocation(null);
        setGeoRadius(250);
        setIncludeQuiz(false);
        setIncludePresentation(false);
        setPresentationType(null);
        setPresentationUrl('');
        setPresentationFile(null);
        setError(null);
        detailsGeneratedRef.current = false;
    };

    const handleClose = () => {
        if (!saving) { reset(); navigate('/badges'); }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) { setImageFile(file); setImagePreview(URL.createObjectURL(file)); }
    };

    const removeImage = () => {
        setImageFile(null); setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSelectType = (type: BadgeType) => {
        setBadgeType(type);
        // Recognition badges are always private
        if (type === 'manual') setIsPublic(false);
        // Reset add-on selections when type changes
        setIncludeQuiz(false);
        setIncludePresentation(false);
        setPresentationType(null);
        setPresentationUrl('');
        setPresentationFile(null);
        // Move to next step after selecting type
        setStepIndex(1);
    };

    // ----- AI handlers -----

    const handleFetchUrl = async (url?: string) => {
        const targetUrl = url ?? sourceUrl;
        if (!targetUrl.trim()) return;
        setFetchingUrl(true);
        setError(null);
        const normalizedUrl = targetUrl.startsWith('http') ? targetUrl : `https://${targetUrl}`;
        setLastFetchedUrl(normalizedUrl);

        // Watch badges with a parseable YouTube URL: fetch the transcript and
        // oEmbed details, use the transcript as the rich text source for
        // generateBadgeDetails. Falls through to the legacy page-scrape path
        // when there's no transcript or the URL doesn't look like YouTube.
        const videoId = badgeType === 'watch' ? extractYouTubeId(normalizedUrl) : null;
        if (videoId) {
            try {
                const [detailsRes, transcriptRes] = await Promise.all([
                    getYoutubeDetails(normalizedUrl),
                    loadYoutubeTranscript(videoId),
                ]);
                if (transcriptRes.success && Array.isArray(transcriptRes.data) && transcriptRes.data.length > 0) {
                    const segments = transcriptRes.data;
                    transcriptCacheRef.current = { videoId, segments };
                    setCachedTranscriptVideoId(videoId);

                    const title = detailsRes.success ? (detailsRes.data?.title || '') : '';
                    if (title && !badgeName) setBadgeName(title);

                    // Concatenate transcript text. Truncate to ~8000 chars so
                    // we don't blow generateBadgeDetails' context budget.
                    const transcriptText = segments
                        .map(s => s.text)
                        .join(' ')
                        .slice(0, 8000);

                    if (badgeType) {
                        generateBadgeDetails({
                            badgeType,
                            badgeName: title || badgeName || undefined,
                            badgeDescription: stripHtml(badgeDescription).trim() || undefined,
                            textContent: transcriptText,
                        }).then(detRes => {
                            if (!detRes.success || !detRes.data) return;
                            if (detRes.data.name && !badgeName) setBadgeName(detRes.data.name);
                            if (detRes.data.description && !stripHtml(badgeDescription).trim()) setBadgeDescription(detRes.data.description);
                            if (detRes.data.tags && !tags.trim()) setTags(detRes.data.tags);
                        });
                    }
                    setFetchingUrl(false);
                    return;
                }
                // No transcript / unavailable → fall through to legacy path.
            } catch {
                // Network or unexpected failure → fall through to legacy path.
            }
        }

        try {
            const res = await fetchUrlInfo(normalizedUrl);
            if (res.success && res.data) {
                if (res.data.title && !badgeName) setBadgeName(res.data.title);
                if (res.data.description && !stripHtml(badgeDescription).trim()) setBadgeDescription(res.data.description);
                // Auto-generate tags from the page content
                if (!tags.trim() && badgeType) {
                    generateBadgeDetails({
                        badgeType,
                        badgeName: res.data.title || badgeName || undefined,
                        badgeDescription: res.data.description || undefined,
                        textContent: res.data.textContent || undefined,
                    }).then(detailsRes => {
                        if (detailsRes.success && detailsRes.data?.tags && !tags.trim()) {
                            setTags(detailsRes.data.tags);
                        }
                    });
                }
            } else {
                setError(res.message ?? 'Failed to fetch URL info');
            }
        } catch { setError('Failed to fetch URL'); }
        finally { setFetchingUrl(false); }
    };

    // Auto-lookup URL after user stops typing (800ms debounce)
    useEffect(() => {
        if (!sourceUrl.trim()) return;
        // Basic URL validation — must have a dot (e.g. "example.com")
        const trimmed = sourceUrl.trim();
        if (!trimmed.includes('.')) return;
        const normalized = trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;
        if (normalized === lastFetchedUrl) return;

        const timer = setTimeout(() => {
            handleFetchUrl(trimmed);
        }, 800);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceUrl]);

    const handleGenerateQuestions = async (force = false) => {
        if (!sourceUrl.trim()) return;
        const url = sourceUrl.startsWith('http') ? sourceUrl : `https://${sourceUrl}`;
        if (!force && url === lastGeneratedQuestionsUrl && questions.length > 0) return;
        setGeneratingQuestions(true);
        setError(null);
        setLastGeneratedQuestionsUrl(url);
        try {
            const res = await generateQuestions({ url, type: 'website', count: 10 });
            if (res.success && res.data?.questions) {
                setQuestions(res.data.questions);
            } else {
                setError(res.message ?? 'Failed to generate questions');
            }
        } catch { setError('Failed to generate questions'); }
        finally { setGeneratingQuestions(false); }
    };

    // (No auto-generation on step entry — quiz is optional, user clicks "Add Quiz")

    const handleDesignAssistantAccept = async (imageUrl: string) => {
        const [header, b64] = imageUrl.split(',');
        const mime = header.match(/:(.*?);/)?.[1] ?? 'image/png';
        const binary = atob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: mime });
        const file = new File([blob], 'ai-badge.png', { type: mime });
        setImageFile(file);
        setImagePreview(imageUrl);
    };

    const handleGenerateCriteria = async () => {
        if (!badgeName.trim()) { setError('Enter a badge name first'); return; }
        setGeneratingCriteria(true);
        setError(null);
        try {
            const res = await generateCriteria({
                badgeName: badgeName.trim(),
                badgeDescription: stripHtml(badgeDescription).trim(),
            });
            if (res.success && res.data?.criteria) {
                setBadgeCriteria(res.data.criteria);
            } else {
                setError(res.message ?? 'Failed to generate criteria');
            }
        } catch { setError('Failed to generate criteria'); }
        finally { setGeneratingCriteria(false); }
    };

    // ----- Question editing helpers -----

    const updateQuestion = (index: number, field: keyof QuizQuestion, value: unknown) => {
        setQuestions(prev => prev.map((q, i) => i === index ? { ...q, [field]: value } : q));
    };

    const updateOption = (qIndex: number, optIndex: number, value: string) => {
        setQuestions(prev => prev.map((q, i) => {
            if (i !== qIndex) return q;
            const newOptions = [...q.options];
            const wasCorrect = q.correctAnswer === newOptions[optIndex];
            newOptions[optIndex] = value;
            return { ...q, options: newOptions, correctAnswer: wasCorrect ? value : q.correctAnswer };
        }));
    };

    const setCorrectAnswer = (qIndex: number, optIndex: number) => {
        setQuestions(prev => prev.map((q, i) => i === qIndex ? { ...q, correctAnswer: q.options[optIndex] } : q));
    };

    const removeOption = (qIndex: number, optIndex: number) => {
        setQuestions(prev => prev.map((q, i) => {
            if (i !== qIndex || q.options.length <= 2) return q;
            const removed = q.options[optIndex];
            const newOptions = q.options.filter((_, j) => j !== optIndex);
            return { ...q, options: newOptions, correctAnswer: q.correctAnswer === removed ? newOptions[0] : q.correctAnswer };
        }));
    };

    const addOption = (qIndex: number) => {
        setQuestions(prev => prev.map((q, i) => i === qIndex ? { ...q, options: [...q.options, ''] } : q));
    };

    const removeQuestion = (index: number) => {
        setQuestions(prev => prev.filter((_, i) => i !== index));
    };

    // ----- Learning path helpers -----

    // Auto-generate badge name, description, and tags when entering the details step
    useEffect(() => {
        if (currentStepId !== 'details') return;
        if (detailsGeneratedRef.current) return;
        if (!badgeType) return;
        detailsGeneratedRef.current = true;

        setGeneratingDetails(true);
        generateBadgeDetails({
            badgeType,
            badgeName: badgeName || undefined,
            badgeDescription: stripHtml(badgeDescription) || undefined,
            textContent: undefined,
        }).then(res => {
            if (res.success && res.data) {
                if (res.data.name && !badgeName) setBadgeName(res.data.name);
                if (res.data.description && !stripHtml(badgeDescription).trim()) setBadgeDescription(res.data.description);
                if (res.data.tags && !tags.trim()) setTags(res.data.tags);
            }
        }).finally(() => setGeneratingDetails(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStepId]);

    // Load available badges when entering the badges step
    useEffect(() => {
        if (currentStepId !== 'badges') return;
        if (availableBadges.length > 0) return; // already loaded
        setLoadingBadges(true);
        getBadges({ pageSize: 200 }).then(res => {
            if (res.success && res.data) {
                setAvailableBadges(res.data);
            }
        }).finally(() => setLoadingBadges(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStepId]);

    const toggleBadgeSelection = (badge: Badge) => {
        setSelectedBadges(prev => {
            const exists = prev.find(b => b.id === badge.badgeID);
            if (exists) return prev.filter(b => b.id !== badge.badgeID);
            return [...prev, { id: badge.badgeID, name: badge.badgeName, isRequired: true, image: badge.badgeImage }];
        });
    };

    const toggleBadgeRequired = (badgeId: string) => {
        setSelectedBadges(prev => prev.map(b =>
            b.id === badgeId ? { ...b, isRequired: !b.isRequired } : b
        ));
    };

    const removeBadgeFromPath = (badgeId: string) => {
        setSelectedBadges(prev => prev.filter(b => b.id !== badgeId));
    };

    const filteredAvailableBadges = useMemo(() => {
        const q = badgeSearchQuery.toLowerCase().trim();
        if (!q) return availableBadges;
        return availableBadges.filter(b =>
            b.badgeName.toLowerCase().includes(q) ||
            (b.badgeDescription && stripHtml(b.badgeDescription).toLowerCase().includes(q))
        );
    }, [availableBadges, badgeSearchQuery]);

    const canAdvance = (): boolean => {
        switch (currentStepId) {
            case 'type':     return !!badgeType;
            case 'url':      return !!sourceUrl.trim();
            case 'details':  return !!issuerID && !!badgeName.trim() && !!stripHtml(badgeDescription).trim();
            case 'badges':   return selectedBadges.length >= 2;
            case 'criteria':     return badgeType !== 'geo' || !!geoLocation;
            case 'presentation': {
                if (!presentationType) return false;
                if (presentationType === 'youtube') return !!presentationUrl.trim();
                return !!presentationFile;
            }
            default:         return true;
        }
    };

    const handleNext = () => { setError(null); if (!isLastStep) setStepIndex(s => s + 1); };
    const handleBack = () => {
        setError(null);
        if (stepIndex > 0) {
            // If going back to type step, reset type so user can re-pick
            if (steps[stepIndex - 1] === 'type') {
                setBadgeType(null);
            }
            setStepIndex(s => s - 1);
        }
    };

    // Build the objective JSON for read/watch/geo/multi badges
    const buildBadgeObjective = (): object | undefined => {
        if (badgeType === 'geo' && geoLocation) {
            return {
                type: 'geo',
                latitude: geoLocation.lat,
                longitude: geoLocation.lng,
                radius: geoRadius,
            };
        }
        if (badgeType === 'multi') {
            return {
                type: 'LearningPath',
                learningPath: selectedBadges.map(b => ({
                    id: b.id,
                    name: b.name,
                    isRequired: b.isRequired,
                })),
                optionalRequiredCount,
            };
        }
        if (badgeType !== 'read' && badgeType !== 'watch') {
            // Earn Now (instant) badges can have an optional YouTube presentation and/or quiz.
            // Return their data so the server stores Pages.Url — without this the badge
            // would auto-award on click instead of routing through the watch page.
            if (badgeType === 'instant') {
                const obj: Record<string, unknown> = {};
                if (includePresentation && presentationType) {
                    obj.presentation = {
                        type: presentationType,
                        url: presentationType === 'youtube' ? presentationUrl.trim() : null,
                        fileName: presentationFile?.name ?? null,
                    };
                }
                if (includeQuiz && questions.length > 0) {
                    obj.quizData = {
                        quizScore,
                        quizQuestions: questions.map((q, i) => ({
                            type: 'choice' as const,
                            randomize: randomizeQuestions,
                            id: `q-${i}`,
                            questionText: q.question,
                            options: q.options.map(opt => ({
                                answer: opt,
                                isCorrect: opt === q.correctAnswer,
                            })),
                            feedback: q.feedback,
                            index: i,
                        })),
                        timedQuestion: timedQuestions,
                        randomizingQuestions: randomizeQuestions,
                        pickQuestionsAmount: questions.length,
                    };
                }
                if (Object.keys(obj).length > 0) return obj;
            }
            return undefined;
        }
        const url = sourceUrl.startsWith('http') ? sourceUrl : `https://${sourceUrl}`;

        // Convert AI-generated questions to schema format
        const quizQuestions = questions.map((q, i) => ({
            type: 'choice' as const,
            randomize: randomizeQuestions,
            id: `q-${i}`,
            questionText: q.question,
            options: q.options.map(opt => ({
                answer: opt,
                isCorrect: opt === q.correctAnswer,
            })),
            feedback: q.feedback,
            index: i,
        }));

        return {
            type: 'visit-scroll',
            pageUrl: url,
            videoUrl: badgeType === 'watch' ? url : null,
            videoDuration: null,
            videoTitle: badgeType === 'watch' ? badgeName : null,
            quizData: quizQuestions.length > 0 ? {
                quizScore,
                quizQuestions,
                timedQuestion: timedQuestions,
                randomizingQuestions: randomizeQuestions,
                pickQuestionsAmount: quizQuestions.length,
            } : null,
            scrollPercentage: trackingMode === 'auto' ? scrollPercentage : null,
            signoff: trackingMode === 'signoff',
            signoffText: trackingMode === 'signoff' ? signoffText : null,
            signoffPos: trackingMode === 'signoff' ? signoffPos : null,
            signoffTheme: trackingMode === 'signoff' ? signoffTheme : null,
            signoffPosV: trackingMode === 'signoff' ? signoffPosV : null,
            minReadTime: trackingMode === 'signoff' && minReadTimeEnabled
                ? `${minReadTimeHours.padStart(2, '0')}:${minReadTimeMinutes.padStart(2, '0')}:${minReadTimeSeconds.padStart(2, '0')}`
                : null,
            hasLogin: null,
            loginText: null,
            geoLocation: null,
            order: null,
            presentation: includePresentation && presentationType ? {
                type: presentationType,
                url: presentationType === 'youtube' ? presentationUrl : null,
                fileName: presentationFile?.name ?? null,
            } : null,
        };
    };

    const handleSubmit = async () => {
        setSaving(true);
        setError(null);
        try {
            if (editMode && initialBadge) {
                // Legacy-parity edit: only the OpenBadges scalar columns + Pages.Url.
                // Quiz / presentation / multi-badge children are intentionally not
                // re-sent here — the form doesn't expose those steps in edit mode.
                const res = await updateBadge(initialBadge.badgeID, {
                    badgeName: badgeName.trim(),
                    badgeDescription: badgeDescription.trim(),
                    badgeCriteria: badgeCriteria.trim() || undefined,
                    isPublic: badgeType === 'manual' ? false : isPublic,
                    isActive,
                    tags: tags.trim() || undefined,
                    pageUrl: (badgeType === 'read' || badgeType === 'watch') && sourceUrl.trim()
                        ? (sourceUrl.startsWith('http') ? sourceUrl : `https://${sourceUrl}`)
                        : undefined,
                });
                if (!res.success) { setError(res.message ?? 'Failed to update badge.'); setSaving(false); return; }
                if (imageFile) {
                    try {
                        const imgRes = await uploadBadgeImage(initialBadge.badgeID, imageFile);
                        if (!imgRes.success) {
                            console.warn('Badge updated but image upload failed:', imgRes.message);
                        }
                    } catch (imgErr) {
                        console.warn('Badge updated but image upload threw:', imgErr);
                    }
                }
                navigate(`/badges/${initialBadge.badgeID}`);
                return;
            }

            const res = await createBadge({
                badgeName: badgeName.trim(),
                badgeDescription: badgeDescription.trim(),
                badgeCriteria: badgeCriteria.trim() || undefined,
                isPublic: badgeType === 'manual' ? false : isPublic,
                tags: tags.trim() || undefined,
                issuerID: issuerID || undefined,
                badgeType: badgeType || undefined,
                badgeObjective: buildBadgeObjective(),
            });
            if (!res.success) { setError(res.message ?? 'Failed to create badge.'); setSaving(false); return; }
            if (imageFile && res.data?.badgeID) {
                try {
                    const imgRes = await uploadBadgeImage(res.data.badgeID, imageFile);
                    if (!imgRes.success) {
                        console.warn('Badge created but image upload failed:', imgRes.message);
                    }
                } catch (imgErr) {
                    console.warn('Badge created but image upload threw:', imgErr);
                }
            }
            // Watch badges: persist whatever questions the admin authored to the
            // new badge — regardless of the timedQuestions toggle, since Watch
            // always sources questions from the transcript now. Failure is
            // reported but doesn't roll back the badge itself.
            if (badgeType === 'watch' && timedQuestionsList.length > 0 && res.data?.badgeID) {
                try {
                    const tqRes = await saveTimedQuestions(res.data.badgeID, {
                        questions:    timedQuestionsList,
                        passingScore: parseInt(quizScore, 10) || 80,
                    });
                    if (!tqRes.success) {
                        console.warn('Badge created but timed-questions save failed:', tqRes.message);
                        setError(`Badge created but questions failed to save: ${tqRes.message ?? 'unknown error'}`);
                        setSaving(false);
                        return;
                    }
                } catch (tqErr) {
                    console.warn('Badge created but timed-questions save threw:', tqErr);
                    setError('Badge created but questions failed to save.');
                    setSaving(false);
                    return;
                }
            }
            reset(); navigate('/badges');
        } catch { setError('An unexpected error occurred.'); } finally { setSaving(false); }
    };

    const selectedIssuer = issuers.find(i => i.issuerID === issuerID);
    const tagList = tags.split(',').map(t => t.trim()).filter(Boolean);
    const typeLabel = BADGE_TYPES.find(t => t.type === badgeType)?.label ?? '';

    // ---------------------------------------------------------------------------
    // Step renderers
    // ---------------------------------------------------------------------------

    const renderStep = () => {
        switch (currentStepId) {
            // ----- Badge Type Selection -----
            case 'type':
                return (
                    <Box>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Choose the type of badge you want to create.
                        </Typography>
                        <Grid container spacing={2}>
                            {BADGE_TYPES.map(opt => (
                                <Grid item xs={6} sm={4} key={opt.type}>
                                    <Card
                                        variant={badgeType === opt.type ? 'elevation' : 'outlined'}
                                        sx={{
                                            height: '100%',
                                            border: badgeType === opt.type ? '2px solid' : undefined,
                                            borderColor: badgeType === opt.type ? 'primary.main' : undefined,
                                        }}
                                    >
                                        <CardActionArea
                                            onClick={() => handleSelectType(opt.type)}
                                            sx={{ height: '100%', p: 1 }}
                                        >
                                            <CardContent sx={{ textAlign: 'center', p: 1 }}>
                                                <Box sx={{ color: badgeType === opt.type ? 'primary.main' : 'text.secondary', mb: 0.5 }}>
                                                    {ICON_BY_TYPE[opt.type]}
                                                </Box>
                                                <Typography variant="subtitle2" fontWeight={600}>
                                                    {opt.label}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {opt.description}
                                                </Typography>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                );

            // ----- Source URL (read / watch) -----
            case 'url':
                return (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Typography variant="body2" color="text.secondary">
                            {badgeType === 'watch'
                                ? 'Enter the YouTube or video URL for this badge.'
                                : 'Enter the webpage URL learners must read to earn this badge.'}
                        </Typography>
                        <TextField
                            label={badgeType === 'watch' ? 'Video URL' : 'Webpage URL'}
                            value={sourceUrl}
                            onChange={e => setSourceUrl(e.target.value)}
                            required
                            fullWidth
                            autoFocus
                            placeholder={badgeType === 'watch' ? 'www.youtube.com/watch?v=...' : 'www.example.com/article'}
                            inputProps={{ maxLength: 2000 }}
                            InputProps={{
                                endAdornment: fetchingUrl ? <CircularProgress size={20} /> : undefined,
                            }}
                        />
                        {badgeName && (
                            <Alert severity="success" sx={{ py: 0.5 }}>
                                Found: <strong>{badgeName}</strong>
                            </Alert>
                        )}
                    </Box>
                );

            // ----- Details -----
            case 'details':
                return (
                    <Box display="flex" flexDirection="column" gap={2}>
                        {generatingDetails && (
                            <Box display="flex" alignItems="center" gap={1}>
                                <CircularProgress size={14} />
                                <Typography variant="caption" color="text.secondary">Generating badge details…</Typography>
                            </Box>
                        )}
                        <FormControl fullWidth required>
                            <InputLabel>Issuer</InputLabel>
                            <Select
                                label="Issuer"
                                value={issuerID}
                                onChange={e => setIssuerID(e.target.value)}
                                disabled={editMode}
                            >
                                {issuers.map(iss => (
                                    <MenuItem key={iss.issuerID} value={iss.issuerID}>
                                        {iss.issuerName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Badge Name"
                            value={badgeName}
                            onChange={e => setBadgeName(e.target.value)}
                            required
                            fullWidth
                            inputProps={{ maxLength: 200 }}
                        />
                        <Box>
                            <Typography variant="body2" color="text.secondary" mb={0.5}>
                                Description *
                            </Typography>
                            <SimpleHtmlEditor
                                value={badgeDescription}
                                onChange={setBadgeDescription}
                                placeholder="Describe what this badge represents..."
                                minHeight={100}
                            />
                        </Box>
                    </Box>
                );

            // ----- Tags -----
            case 'tags':
                return (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Typography variant="body2" color="text.secondary">
                            Add tags to help people discover your badge. Separate with commas.
                        </Typography>
                        <TextField
                            label="Tags"
                            value={tags}
                            onChange={e => setTags(e.target.value)}
                            fullWidth
                            autoFocus
                            placeholder="e.g. leadership, teamwork, coding"
                            inputProps={{ maxLength: 1000 }}
                        />
                        {tagList.length > 0 && (
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                                {tagList.map((tag, i) => <Chip key={i} label={tag} size="small" />)}
                            </Box>
                        )}
                    </Box>
                );

            // ----- Image -----
            case 'image':
                return (
                    <Box display="flex" flexDirection="column" gap={2} alignItems="center">
                        <Typography variant="body2" color="text.secondary" alignSelf="flex-start">
                            Upload an image for your badge, or generate one with AI.
                        </Typography>
                        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                        {imagePreview ? (
                            <Box textAlign="center">
                                <Box
                                    component="img" src={imagePreview} alt="Preview"
                                    sx={{ maxWidth: 250, maxHeight: 250, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                                />
                                <Box display="flex" justifyContent="center" gap={1} mt={1}>
                                    <Button size="small" onClick={() => fileInputRef.current?.click()}>Change</Button>
                                    <Button size="small" color="error" onClick={removeImage}>Remove</Button>
                                </Box>
                                <Typography variant="caption" color="text.secondary">{imageFile?.name}</Typography>
                            </Box>
                        ) : (
                            <Box
                                onClick={() => fileInputRef.current?.click()}
                                sx={{
                                    width: 250, height: 250, border: '2px dashed', borderColor: 'divider', borderRadius: 2,
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer', '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                                }}
                            >
                                <WorkspacePremiumIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                                <Typography variant="body2" color="text.secondary">Click to upload image</Typography>
                                <Typography variant="caption" color="text.secondary">PNG, JPG, SVG up to 5MB</Typography>
                            </Box>
                        )}
                        <Divider sx={{ width: '100%' }}>or</Divider>
                        <Button
                            color="secondary"
                            onClick={() => setDesignAssistantOpen(true)}
                            disabled={!badgeName.trim()}
                            startIcon={<AutoAwesomeIcon />}
                        >
                            AI Design Assistant
                        </Button>
                        <BadgeDesignAssistant
                            open={designAssistantOpen}
                            badgeName={badgeName}
                            badgeDescription={stripHtml(badgeDescription)}
                            onAccept={handleDesignAssistantAccept}
                            onClose={() => setDesignAssistantOpen(false)}
                        />
                        <Divider sx={{ width: '100%' }}>or</Divider>
                        <Button
                            onClick={() => setEditorOpen(true)}
                        >
                            Design Image
                        </Button>
                        <BadgeEditor
                            open={editorOpen}
                            onClose={() => setEditorOpen(false)}
                            initialImageUrl={imagePreview}
                            onExport={(file, url) => {
                                setImageFile(file);
                                setImagePreview(url);
                                setEditorOpen(false);
                            }}
                        />
                    </Box>
                );

            // ----- Criteria & Settings -----
            case 'criteria':
                return (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                <Typography variant="body2" color="text.secondary">
                                    Criteria — what must someone do to earn this badge?
                                </Typography>
                                <Button
                                    color="secondary"
                                    onClick={handleGenerateCriteria}
                                    disabled={!badgeName.trim() || generatingCriteria}
                                    startIcon={generatingCriteria ? <CircularProgress size={14} /> : <AutoAwesomeIcon fontSize="small" />}
                                >
                                    {generatingCriteria ? 'Generating...' : 'Generate with AI'}
                                </Button>
                            </Box>
                            <SimpleHtmlEditor
                                value={badgeCriteria}
                                onChange={setBadgeCriteria}
                                placeholder="Describe the earning criteria..."
                                minHeight={100}
                            />
                        </Box>
                        {badgeType === 'read' && (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>Read Tracking</Typography>
                                <RadioGroup
                                    value={trackingMode}
                                    onChange={e => setTrackingMode(e.target.value as 'auto' | 'signoff')}
                                >
                                    <FormControlLabel
                                        value="auto"
                                        control={<Radio size="small" />}
                                        label="Automatically track percentage viewed"
                                    />
                                    {trackingMode === 'auto' && (
                                        <Box sx={{ pl: 4, pb: 1 }}>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                Percentage required to scroll for completion: {scrollPercentage}%
                                            </Typography>
                                            <Slider
                                                value={parseInt(scrollPercentage)}
                                                onChange={(_, v) => setScrollPercentage(String(v))}
                                                min={0} max={100} step={10}
                                                marks={[
                                                    { value: 0, label: '0%' },
                                                    { value: 50, label: '50%' },
                                                    { value: 100, label: '100%' },
                                                ]}
                                                valueLabelDisplay="auto"
                                                size="small"
                                            />
                                        </Box>
                                    )}
                                    <FormControlLabel
                                        value="signoff"
                                        control={<Radio size="small" />}
                                        label="Present sign off button to user"
                                    />
                                    {trackingMode === 'signoff' && (
                                        <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column', gap: 2, pb: 1 }}>
                                            <TextField
                                                label="Button Text"
                                                value={signoffText}
                                                onChange={e => setSignoffText(e.target.value)}
                                                fullWidth
                                                size="small"
                                            />
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>Button Theme</Typography>
                                                <RadioGroup row value={signoffTheme} onChange={e => setSignoffTheme(e.target.value as 'dark' | 'light')}>
                                                    <FormControlLabel value="dark" control={<Radio size="small" />} label="Dark" />
                                                    <FormControlLabel value="light" control={<Radio size="small" />} label="Light" />
                                                </RadioGroup>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>Button Position</Typography>
                                                <RadioGroup row value={signoffPos} onChange={e => setSignoffPos(e.target.value as 'left' | 'middle' | 'right')}>
                                                    <FormControlLabel value="left" control={<Radio size="small" />} label="Left" />
                                                    <FormControlLabel value="middle" control={<Radio size="small" />} label="Middle" />
                                                    <FormControlLabel value="right" control={<Radio size="small" />} label="Right" />
                                                </RadioGroup>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>Button Bottom Position</Typography>
                                                <RadioGroup row value={signoffPosV} onChange={e => setSignoffPosV(e.target.value as 'fixed' | 'sticky')}>
                                                    <FormControlLabel value="fixed" control={<Radio size="small" />} label="Fixed Bottom" />
                                                    <FormControlLabel value="sticky" control={<Radio size="small" />} label="Sticky" />
                                                </RadioGroup>
                                            </Box>
                                            <FormControlLabel
                                                control={<Switch checked={minReadTimeEnabled} onChange={e => setMinReadTimeEnabled(e.target.checked)} size="small" />}
                                                label="Require a minimum read time before sign off button becomes active"
                                            />
                                            {minReadTimeEnabled && (
                                                <Box display="flex" gap={1} alignItems="center">
                                                    <TextField
                                                        label="Hours"
                                                        value={minReadTimeHours}
                                                        onChange={e => setMinReadTimeHours(e.target.value)}
                                                        type="number"
                                                        size="small"
                                                        inputProps={{ min: 0, max: 24 }}
                                                        sx={{ width: 90 }}
                                                    />
                                                    <TextField
                                                        label="Minutes"
                                                        value={minReadTimeMinutes}
                                                        onChange={e => setMinReadTimeMinutes(e.target.value)}
                                                        type="number"
                                                        size="small"
                                                        inputProps={{ min: 0, max: 59 }}
                                                        sx={{ width: 90 }}
                                                    />
                                                    <TextField
                                                        label="Seconds"
                                                        value={minReadTimeSeconds}
                                                        onChange={e => setMinReadTimeSeconds(e.target.value)}
                                                        type="number"
                                                        size="small"
                                                        inputProps={{ min: 0, max: 59 }}
                                                        sx={{ width: 90 }}
                                                    />
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </RadioGroup>
                            </Box>
                        )}
                        {badgeType === 'geo' && (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Badge Location
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={1.5}>
                                    Set the physical location where learners must check in to earn this badge.
                                </Typography>
                                <LocationPicker
                                    value={geoLocation}
                                    radius={geoRadius}
                                    onChange={setGeoLocation}
                                    onRadiusChange={setGeoRadius}
                                />
                                {initialBadge?.geoLocation?.locationImages && initialBadge.geoLocation.locationImages.length > 0 && (
                                    <Box mt={2}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Location reference photos
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" mb={1}>
                                            Submitted from the mobile app to help you finish this badge.
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {initialBadge.geoLocation.locationImages.map((url, i) => (
                                                <Box
                                                    key={i}
                                                    component="img"
                                                    src={url}
                                                    alt={`Location reference ${i + 1}`}
                                                    sx={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                                {!geoLocation && (
                                    <Typography variant="caption" color="error" mt={1} display="block">
                                        A location is required to continue
                                    </Typography>
                                )}
                            </Box>
                        )}
                        {/* Optional add-ons — all types except Recognition and Multi Badge */}
                        {SUPPORTS_ADDONS.includes(badgeType!) && (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>Optional Add-ons</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Card
                                            variant="outlined"
                                            onClick={() => setIncludeQuiz(v => !v)}
                                            sx={{
                                                cursor: 'pointer',
                                                border: '2px solid',
                                                borderColor: includeQuiz ? 'primary.main' : 'divider',
                                                transition: 'border-color 0.15s',
                                            }}
                                        >
                                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: '12px !important' }}>
                                                <QuizIcon sx={{ fontSize: 32, color: includeQuiz ? 'primary.main' : 'text.secondary', flexShrink: 0 }} />
                                                <Box flex={1}>
                                                    <Typography variant="subtitle2">Activity Quiz</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Add questions learners must answer
                                                    </Typography>
                                                </Box>
                                                <Checkbox
                                                    checked={includeQuiz}
                                                    onChange={() => {}}
                                                    size="small"
                                                    onClick={e => e.stopPropagation()}
                                                    color="primary"
                                                />
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Card
                                            variant="outlined"
                                            onClick={() => setIncludePresentation(v => !v)}
                                            sx={{
                                                cursor: 'pointer',
                                                border: '2px solid',
                                                borderColor: includePresentation ? 'primary.main' : 'divider',
                                                transition: 'border-color 0.15s',
                                            }}
                                        >
                                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: '12px !important' }}>
                                                <SlideshowIcon sx={{ fontSize: 32, color: includePresentation ? 'primary.main' : 'text.secondary', flexShrink: 0 }} />
                                                <Box flex={1}>
                                                    <Typography variant="subtitle2">Presentation</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Attach a video, slideshow, or SCORM
                                                    </Typography>
                                                </Box>
                                                <Checkbox
                                                    checked={includePresentation}
                                                    onChange={() => {}}
                                                    size="small"
                                                    onClick={e => e.stopPropagation()}
                                                    color="primary"
                                                />
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                        {badgeType !== 'manual' && (
                            <FormControlLabel
                                control={<Switch checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />}
                                label="Public badge (visible in Available Badges)"
                            />
                        )}
                        {badgeType === 'manual' && (
                            <Typography variant="body2" color="text.secondary">
                                Recognition badges are always private.
                            </Typography>
                        )}
                        {editMode && (
                            <FormControlLabel
                                control={<Switch checked={isActive} onChange={e => setIsActive(e.target.checked)} />}
                                label={isActive ? 'Active' : 'Inactive (hidden from Available Badges and Earn flows)'}
                            />
                        )}
                    </Box>
                );

            // ----- Presentation -----
            case 'presentation': {
                const PRESENTATION_TYPES = [
                    {
                        type: 'youtube' as const,
                        label: 'YouTube Video',
                        icon: <OndemandVideoIcon sx={{ fontSize: 36 }} />,
                        description: 'Link to a YouTube video',
                    },
                    {
                        type: 'powerpoint' as const,
                        label: 'PowerPoint',
                        icon: <SlideshowIcon sx={{ fontSize: 36 }} />,
                        description: 'Upload a .pptx file',
                    },
                    {
                        type: 'scorm' as const,
                        label: 'SCORM Package',
                        icon: <FolderZipIcon sx={{ fontSize: 36 }} />,
                        description: 'Upload a SCORM .zip',
                    },
                ];
                return (
                    <Box display="flex" flexDirection="column" gap={3}>
                        <Typography variant="body2" color="text.secondary">
                            Select the type of presentation to attach to this badge.
                        </Typography>

                        {/* Type selection */}
                        <Grid container spacing={2}>
                            {PRESENTATION_TYPES.map(opt => (
                                <Grid item xs={12} sm={4} key={opt.type}>
                                    <Card
                                        variant="outlined"
                                        onClick={() => { setPresentationType(opt.type); setPresentationUrl(''); setPresentationFile(null); }}
                                        sx={{
                                            cursor: 'pointer',
                                            border: '2px solid',
                                            borderColor: presentationType === opt.type ? 'primary.main' : 'divider',
                                            transition: 'border-color 0.15s',
                                        }}
                                    >
                                        <CardContent sx={{ textAlign: 'center', py: 3 }}>
                                            <Box sx={{ color: presentationType === opt.type ? 'primary.main' : 'text.secondary' }}>
                                                {opt.icon}
                                            </Box>
                                            <Typography variant="subtitle2" mt={1}>{opt.label}</Typography>
                                            <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>

                        {/* YouTube URL input */}
                        {presentationType === 'youtube' && (
                            <TextField
                                label="YouTube URL"
                                value={presentationUrl}
                                onChange={e => setPresentationUrl(e.target.value)}
                                size="small"
                                fullWidth
                                placeholder="https://www.youtube.com/watch?v=..."
                                helperText="Paste the full YouTube video URL"
                            />
                        )}

                        {/* File upload for PowerPoint / SCORM */}
                        {(presentationType === 'powerpoint' || presentationType === 'scorm') && (
                            <Box>
                                <input
                                    type="file"
                                    accept={presentationType === 'powerpoint' ? '.ppt,.pptx' : '.zip'}
                                    ref={presentationFileRef}
                                    style={{ display: 'none' }}
                                    onChange={e => setPresentationFile(e.target.files?.[0] ?? null)}
                                />
                                {presentationFile ? (
                                    <Box
                                        display="flex" alignItems="center" gap={2} p={2}
                                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                                    >
                                        <UploadFileIcon color="primary" />
                                        <Typography variant="body2" flex={1}>{presentationFile.name}</Typography>
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={() => { setPresentationFile(null); if (presentationFileRef.current) presentationFileRef.current.value = ''; }}
                                        >
                                            Remove
                                        </Button>
                                    </Box>
                                ) : (
                                    <Button
                                        startIcon={<UploadFileIcon />}
                                        onClick={() => presentationFileRef.current?.click()}
                                        fullWidth
                                        size="large"
                                    >
                                        Choose {presentationType === 'powerpoint' ? 'PowerPoint file (.pptx)' : 'SCORM package (.zip)'}
                                    </Button>
                                )}
                            </Box>
                        )}
                    </Box>
                );
            }

            // ----- Questions -----
            case 'questions':
                // Watch badges always source questions from the YouTube transcript.
                // The timedQuestions toggle controls runtime behaviour (pause vs.
                // post-video) but the AUTHORING path is the same: transcript →
                // generator → /api/badges/:id/timed-questions.
                if (badgeType === 'watch') {
                    const currentVideoId = extractYouTubeId(sourceUrl);
                    const cachedSegments = transcriptCacheRef.current
                        && transcriptCacheRef.current.videoId === currentVideoId
                        && cachedTranscriptVideoId === currentVideoId
                        ? transcriptCacheRef.current.segments
                        : null;
                    return (
                        <TimedQuestionsStep
                            videoUrl={sourceUrl}
                            questions={timedQuestionsList}
                            onChange={setTimedQuestionsList}
                            cachedTranscript={cachedSegments}
                        />
                    );
                }
                return (
                    <Box display="flex" flexDirection="column" gap={2}>
                        {generatingQuestions && (
                            <Box display="flex" alignItems="center" gap={1}>
                                <CircularProgress size={20} />
                                <Typography variant="body2" color="text.secondary">
                                    Generating questions{sourceUrl ? ' from the source URL' : ''}...
                                </Typography>
                            </Box>
                        )}

                        {!generatingQuestions && questions.length === 0 && (
                            <Box
                                display="flex" flexDirection="column" alignItems="center" justifyContent="center"
                                sx={{ py: 4, gap: 1.5 }}
                            >
                                {sourceUrl.trim() ? (
                                    <Button
                                        startIcon={<AutoAwesomeIcon />}
                                        onClick={() => handleGenerateQuestions(true)}
                                        disabled={generatingQuestions}
                                    >
                                        Generate Questions with AI
                                    </Button>
                                ) : (
                                    <Button
                                        startIcon={<AddCircleOutlineIcon />}
                                        onClick={() => setQuestions([{ question: '', options: ['', ''], correctAnswer: '', feedback: { correct: '', incorrect: '' } }])}
                                    >
                                        Add Question
                                    </Button>
                                )}
                            </Box>
                        )}

                        {questions.length > 0 && (
                            <>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle2">
                                        {questions.length} Question{questions.length !== 1 ? 's' : ''}
                                    </Typography>
                                    <Button
                                        color="secondary"
                                        onClick={() => handleGenerateQuestions(true)}
                                        disabled={generatingQuestions}
                                        startIcon={generatingQuestions ? <CircularProgress size={14} /> : <AutoAwesomeIcon fontSize="small" />}
                                    >
                                        Regenerate
                                    </Button>
                                </Box>

                                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                                    {questions.map((q, qi) => (
                                        <Box key={qi} sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                                            <Box display="flex" alignItems="flex-start" gap={1}>
                                                <Typography variant="body2" fontWeight={600} sx={{ mt: 1, minWidth: 24 }}>
                                                    {qi + 1}.
                                                </Typography>
                                                <TextField
                                                    value={q.question}
                                                    onChange={e => updateQuestion(qi, 'question', e.target.value)}
                                                    fullWidth
                                                    size="small"
                                                    multiline
                                                />
                                                <IconButton size="small" color="error" onClick={() => removeQuestion(qi)} title="Remove question">
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            <Box sx={{ pl: 4, mt: 1 }} display="flex" flexDirection="column" gap={0.5}>
                                                {q.options.map((opt, oi) => (
                                                    <Box key={oi} display="flex" alignItems="center" gap={0.5}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setCorrectAnswer(qi, oi)}
                                                            color={opt === q.correctAnswer ? 'success' : 'default'}
                                                            title={opt === q.correctAnswer ? 'Correct answer' : 'Set as correct'}
                                                        >
                                                            {opt === q.correctAnswer
                                                                ? <RadioButtonCheckedIcon fontSize="small" />
                                                                : <RadioButtonUncheckedIcon fontSize="small" />}
                                                        </IconButton>
                                                        <TextField
                                                            value={opt}
                                                            onChange={e => updateOption(qi, oi, e.target.value)}
                                                            fullWidth
                                                            size="small"
                                                            sx={{ '& .MuiInputBase-input': { py: 0.5 } }}
                                                        />
                                                        {q.options.length > 2 && (
                                                            <IconButton size="small" onClick={() => removeOption(qi, oi)} title="Remove option">
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ))}
                                                <Button
                                                    size="small"
                                                    startIcon={<AddCircleOutlineIcon fontSize="small" />}
                                                    onClick={() => addOption(qi)}
                                                    sx={{ alignSelf: 'flex-start', ml: 4 }}
                                                >
                                                    Add Option
                                                </Button>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>

                                <Divider />
                                <Typography variant="subtitle2">Quiz Settings</Typography>

                                <Box display="flex" flexDirection="column" gap={1.5}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Pass Score: {quizScore}%
                                        </Typography>
                                        <Slider
                                            value={parseInt(quizScore)}
                                            onChange={(_, v) => setQuizScore(String(v))}
                                            min={10} max={100} step={10}
                                            marks={[
                                                { value: 10, label: '10%' },
                                                { value: 50, label: '50%' },
                                                { value: 80, label: '80%' },
                                                { value: 100, label: '100%' },
                                            ]}
                                            valueLabelDisplay="auto"
                                            size="small"
                                        />
                                    </Box>
                                    <FormControlLabel
                                        control={<Switch checked={randomizeQuestions} onChange={e => setRandomizeQuestions(e.target.checked)} size="small" />}
                                        label="Randomize question order"
                                    />
                                    <FormControlLabel
                                        control={<Switch checked={timedQuestions} onChange={e => setTimedQuestions(e.target.checked)} size="small" />}
                                        label="Timed questions"
                                    />
                                </Box>
                            </>
                        )}
                    </Box>
                );

            // ----- Select Badges (multi / learning path) -----
            case 'badges': {
                const optionalCount = selectedBadges.filter(b => !b.isRequired).length;
                return (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Typography variant="body2" color="text.secondary">
                            Select the badges that make up this learning path. Learners must earn all required badges
                            {optionalCount > 0 ? ` and a minimum number of optional badges` : ''} to complete it.
                        </Typography>

                        {/* Selected badges */}
                        {selectedBadges.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Selected ({selectedBadges.length})
                                </Typography>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    {selectedBadges.map(b => (
                                        <Box
                                            key={b.id}
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}
                                        >
                                            {b.image ? (
                                                <Box
                                                    component="img" src={b.image} alt={b.name}
                                                    sx={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 0.5 }}
                                                />
                                            ) : (
                                                <WorkspacePremiumIcon sx={{ fontSize: 36, opacity: 0.4 }} />
                                            )}
                                            <Typography variant="body2" fontWeight={600} flex={1}>
                                                {b.name}
                                            </Typography>
                                            <Chip
                                                label={b.isRequired ? 'Required' : 'Optional'}
                                                size="small"
                                                color={b.isRequired ? 'primary' : 'default'}
                                                onClick={() => toggleBadgeRequired(b.id)}
                                                variant={b.isRequired ? 'filled' : 'outlined'}
                                            />
                                            <IconButton size="small" color="error" onClick={() => removeBadgeFromPath(b.id)}>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>

                                {/* Optional required count — only show when there are optional badges */}
                                {optionalCount > 0 && (
                                    <Box mt={2}>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            How many optional badges must be completed? (0 = none required)
                                        </Typography>
                                        <Slider
                                            value={optionalRequiredCount}
                                            onChange={(_, v) => setOptionalRequiredCount(v as number)}
                                            min={0}
                                            max={optionalCount}
                                            step={1}
                                            marks={Array.from({ length: optionalCount + 1 }, (_, i) => ({ value: i, label: String(i) }))}
                                            valueLabelDisplay="auto"
                                            size="small"
                                            sx={{ maxWidth: 300 }}
                                        />
                                    </Box>
                                )}

                                <Divider sx={{ my: 1 }} />
                            </Box>
                        )}

                        {/* Search & browse available badges */}
                        <TextField
                            label="Search badges"
                            value={badgeSearchQuery}
                            onChange={e => setBadgeSearchQuery(e.target.value)}
                            fullWidth
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AutoAwesomeIcon fontSize="small" sx={{ opacity: 0.4 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {loadingBadges && (
                            <Box display="flex" justifyContent="center" py={3}>
                                <CircularProgress size={28} />
                            </Box>
                        )}

                        {!loadingBadges && (
                            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                                {filteredAvailableBadges.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                        No badges found.
                                    </Typography>
                                )}
                                {filteredAvailableBadges.map(b => {
                                    const isSelected = selectedBadges.some(sb => sb.id === b.badgeID);
                                    return (
                                        <Box
                                            key={b.badgeID}
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            sx={{
                                                p: 1,
                                                cursor: 'pointer',
                                                borderRadius: 1,
                                                '&:hover': { bgcolor: 'action.hover' },
                                                bgcolor: isSelected ? 'action.selected' : undefined,
                                            }}
                                            onClick={() => toggleBadgeSelection(b)}
                                        >
                                            <Checkbox
                                                checked={isSelected}
                                                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                size="small"
                                                sx={{ p: 0.5 }}
                                            />
                                            {b.badgeImage ? (
                                                <Box
                                                    component="img" src={b.badgeImage} alt={b.badgeName}
                                                    sx={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 0.5 }}
                                                />
                                            ) : (
                                                <WorkspacePremiumIcon sx={{ fontSize: 32, opacity: 0.3 }} />
                                            )}
                                            <Box flex={1} minWidth={0}>
                                                <Typography variant="body2" fontWeight={500} noWrap>
                                                    {b.badgeName}
                                                </Typography>
                                                {b.issuerName && (
                                                    <Typography variant="caption" color="text.secondary" noWrap>
                                                        {b.issuerName}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}

                        {selectedBadges.length < 2 && (
                            <Alert severity="info" sx={{ py: 0.5 }}>
                                Select at least 2 badges for the learning path.
                            </Alert>
                        )}
                    </Box>
                );
            }

            // ----- Preview -----
            case 'preview':
                return (
                    <Box>
                        <Box display="flex" gap={2} mb={2}>
                            {imagePreview ? (
                                <Box
                                    component="img" src={imagePreview} alt="Badge"
                                    sx={{ width: 120, height: 120, objectFit: 'contain', borderRadius: 1, bgcolor: PURPLE_AVATAR, p: 1 }}
                                />
                            ) : (
                                <Box sx={{ width: 120, height: 120, borderRadius: 1, bgcolor: PURPLE_AVATAR, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <WorkspacePremiumIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                                </Box>
                            )}
                            <Box flex={1}>
                                <Typography variant="h6" fontWeight={600}>{badgeName}</Typography>
                                {selectedIssuer && (
                                    <Typography variant="body2" color="text.secondary">
                                        Issued by {selectedIssuer.issuerName}
                                    </Typography>
                                )}
                                <Box display="flex" gap={0.5} mt={0.5}>
                                    <Chip label={typeLabel} size="small" color="primary" variant="outlined" />
                                    <Chip
                                        label={badgeType === 'manual' ? 'Private' : isPublic ? 'Public' : 'Private'}
                                        color={badgeType !== 'manual' && isPublic ? 'success' : 'default'}
                                        size="small"
                                    />
                                </Box>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        <Typography variant="subtitle2" gutterBottom>Description</Typography>
                        <Typography variant="body2" color="text.secondary" dangerouslySetInnerHTML={{ __html: badgeDescription }} sx={{ mb: 2 }} />

                        {sourceUrl && (
                            <>
                                <Typography variant="subtitle2" gutterBottom>Source URL</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{sourceUrl}</Typography>
                            </>
                        )}

                        {tagList.length > 0 && (
                            <>
                                <Typography variant="subtitle2" gutterBottom>Tags</Typography>
                                <Box display="flex" flexWrap="wrap" gap={0.5} mb={2}>
                                    {tagList.map((tag, i) => <Chip key={i} label={tag} size="small" variant="outlined" />)}
                                </Box>
                            </>
                        )}

                        {stripHtml(badgeCriteria).trim() && (
                            <>
                                <Typography variant="subtitle2" gutterBottom>Criteria</Typography>
                                <Typography variant="body2" color="text.secondary" dangerouslySetInnerHTML={{ __html: badgeCriteria }} sx={{ mb: 2 }} />
                            </>
                        )}

                        {questions.length > 0 && (
                            <>
                                <Typography variant="subtitle2" gutterBottom>Quiz</Typography>
                                <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                                    <Chip label={`${questions.length} Questions`} size="small" variant="outlined" />
                                    <Chip label={`Pass: ${quizScore}%`} size="small" variant="outlined" />
                                    <Chip label={`Scroll: ${scrollPercentage}%`} size="small" variant="outlined" />
                                    {randomizeQuestions && <Chip label="Randomized" size="small" variant="outlined" />}
                                    {timedQuestions && <Chip label="Timed" size="small" variant="outlined" />}
                                    {trackingMode === 'signoff' && <Chip label="Sign-off Required" size="small" variant="outlined" />}
                                </Box>
                            </>
                        )}

                        {selectedBadges.length > 0 && (
                            <>
                                <Typography variant="subtitle2" gutterBottom>Learning Path</Typography>
                                <Box display="flex" flexDirection="column" gap={0.5} mb={1}>
                                    {selectedBadges.map(b => (
                                        <Box key={b.id} display="flex" alignItems="center" gap={1}>
                                            <WorkspacePremiumIcon sx={{ fontSize: 18, opacity: 0.5 }} />
                                            <Typography variant="body2">{b.name}</Typography>
                                            <Chip
                                                label={b.isRequired ? 'Required' : 'Optional'}
                                                size="small"
                                                variant="outlined"
                                                color={b.isRequired ? 'primary' : 'default'}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                                {selectedBadges.some(b => !b.isRequired) && optionalRequiredCount > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                        Must complete at least {optionalRequiredCount} optional badge{optionalRequiredCount !== 1 ? 's' : ''}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            {/* Page header */}
            <Box display="flex" alignItems="center" mb={3} gap={1}>
                <IconButton onClick={handleClose} disabled={saving} aria-label="Back to badges">
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" fontWeight={700}>
                    {editMode
                        ? `Edit ${typeLabel || 'Badge'}`
                        : badgeType ? `New ${typeLabel}` : 'Create Badge'}
                </Typography>
                {editMode && typeLabel && (
                    <Chip
                        label={`Type: ${typeLabel} (locked)`}
                        size="small"
                        sx={{ ml: 1 }}
                    />
                )}
            </Box>

            {/* Stepper */}
            {badgeType && (
                <Stepper activeStep={stepIndex} alternativeLabel sx={{ mb: 4 }}>
                    {stepLabels.map(label => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Step content */}
            <Box sx={{ mb: 10 }}>
                {renderStep()}
            </Box>

            {/* Sticky action bar */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: 'background.paper',
                    borderTop: 1,
                    borderColor: 'divider',
                    px: 4,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    zIndex: 1200,
                }}
            >
                <Button color="secondary" onClick={handleClose} disabled={saving}>Cancel</Button>
                <Box flex={1} />
                {stepIndex > 0 && (
                    <Button color="secondary" onClick={handleBack} disabled={saving}>Back</Button>
                )}
                {currentStepId !== 'type' && !isLastStep && (
                    <Button onClick={handleNext} disabled={!canAdvance()}>
                        Next
                    </Button>
                )}
                {isLastStep && currentStepId !== 'type' && (
                    <Button
                        onClick={handleSubmit}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} /> : undefined}
                    >
                        {saving
                            ? (editMode ? 'Saving...' : 'Creating...')
                            : (editMode ? 'Save Changes' : 'Create Badge')}
                    </Button>
                )}
            </Box>
        </Container>
    );
}
